import { action, observable, runInAction, computed, makeObservable } from 'mobx'
import { FetchResult, FetchStatus } from '~shared/lib/Types/FetchTypes'
import { KdsConfig, KdsConfigResponse } from '../Types/Types'
import dayjs from 'dayjs'
import {
  fetchConfig as fetchConfigFromApi,
  recordHeartbeat as recordHeartbeatToApi,
} from '../Services/DeviceService'
import { isDeviceRevokedError } from '../Utils/ApiErrorUtil'
import { normalizeKdsConfig } from '../Utils/kdsActionPolicy'

// Field order is deterministic (server response order + normalizeKdsConfig's fixed key order),
// so a straight stringify comparison is safe and avoids pulling in a deep-equal dependency.
function configsEqual(a: KdsConfig | undefined, b: KdsConfig): boolean {
  if (!a) return false
  return JSON.stringify(a) === JSON.stringify(b)
}

class GlobalStore {
  apiUrl = import.meta.env.VITE_API_URL as string

  configFetch: FetchResult<KdsConfig> = {
    status: FetchStatus.unloaded,
    lastFetch: dayjs().format(),
  }

  deviceRevoked = false

  constructor() {
    makeObservable(this, {
      apiUrl: observable,
      configFetch: observable,
      deviceRevoked: observable,
      getConfig: computed,
      fetchConfig: action,
      refreshConfig: action,
      setDeviceRevoked: action,
    })
  }

  get getConfig() {
    return this.configFetch
  }

  setDeviceRevoked(revoked: boolean) {
    this.deviceRevoked = revoked
  }

  private async getConfigData(): Promise<KdsConfig> {
    const raw: KdsConfigResponse = await fetchConfigFromApi()
    return { ...raw, actionPolicy: normalizeKdsConfig(raw) }
  }

  async fetchConfig() {
    this.configFetch = {
      status: FetchStatus.fetching,
      lastFetch: dayjs().format(),
    }

    try {
      const data = await this.getConfigData()

      runInAction(() => {
        this.configFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data,
        }
      })
    } catch (error) {
      runInAction(() => {
        this.configFetch = {
          status: FetchStatus.error,
          lastFetch: dayjs().format(),
        }
      })
      throw error
    }
  }

  // Keeps the last-good config in place on failure so a background refresh never blanks the running display.
  // Also skips the update entirely when the fetched config hasn't actually changed, so unrelated
  // observers (e.g. the SignalR connection effect) don't tear down and rebuild every poll.
  async refreshConfig() {
    try {
      const data = await this.getConfigData()

      if (configsEqual(this.configFetch.data, data)) {
        return
      }

      runInAction(() => {
        this.configFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data,
        }
      })
    } catch (error) {
      window.appInsights?.trackException({ exception: error as Error })
    }
  }

  async recordHeartbeat() {
    try {
      await recordHeartbeatToApi()
    } catch (error) {
      if (isDeviceRevokedError(error)) {
        runInAction(() => {
          this.deviceRevoked = true
        })
        return
      }
      window.appInsights?.trackException({ exception: error as Error })
    }
  }
}

export default new GlobalStore()
